import { computed, inject, onBeforeUnmount, shallowRef } from 'vue';
import { fileDropTargetContext } from './dropTargetContext';

export interface NativeDropTargetOptions<T> {
  /** Unconfigured components leave native events to their surrounding area. */
  enabled: () => boolean;
  key: (target: T) => string | number;
  /**
   * Whether this target takes part in the drag at all. Asked before anything
   * else: a drag it does not take part in is left alone — no `preventDefault`,
   * no `stopPropagation`, no `dropEffect`, no feedback — so it reaches nested
   * editable fields and the surrounding area unchanged. Default: every drag.
   * `canDrop` is the permission check of a drag the target takes part in.
   */
  accepts?: (event: DragEvent) => boolean;
  canDrop: (event: DragEvent, target: T) => boolean;
  onDrop: (event: DragEvent, target: T) => void;
  label: (target: T) => string;
}

/**
 * Shared native target behaviour for cards, table rows and Miller-column rows.
 * The consumer owns the payload and permission checks. A configured target
 * consumes refused drops too (unless `accepts` leaves the drag alone): they must not become an upload to its parent.
 * Accepted targets alone get feedback. Drop rechecks the current permission,
 * and document-level completion clears feedback even when another target
 * handles the event or a drag is cancelled outside this component.
 */
export function useNativeDropTarget<T>(options: NativeDropTargetOptions<T>) {
  const activeTarget = shallowRef<T | null>(null);
  const context = inject(fileDropTargetContext, null);
  const token = Symbol('nx-native-drop-target');
  let element: HTMLElement | null = null;
  let depth = 0;
  let listening = false;

  const label = computed(() =>
    activeTarget.value === null ? '' : options.label(activeTarget.value)
  );

  function reset() {
    activeTarget.value = null;
    element = null;
    depth = 0;
    context?.release(token);
    if (!listening) return;
    listening = false;
    document.removeEventListener('drop', reset, true);
    document.removeEventListener('dragend', reset, true);
    window.removeEventListener('blur', reset);
  }

  function track(event: DragEvent) {
    const next = event.currentTarget;
    if (next instanceof HTMLElement && element !== next) {
      element = next;
      depth = 0;
    }
    context?.claim(token);
    if (listening || typeof document === 'undefined') return;
    listening = true;
    document.addEventListener('drop', reset, true);
    document.addEventListener('dragend', reset, true);
    window.addEventListener('blur', reset);
  }

  const takesPart = (event: DragEvent) => options.accepts?.(event) ?? true;

  function consider(event: DragEvent, target: T) {
    if (!options.enabled() || !takesPart(event)) return false;
    track(event);
    const accepted = options.canDrop(event, target);
    activeTarget.value = accepted ? target : null;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = accepted
        ? Array.from(event.dataTransfer.types).includes('Files')
          ? 'copy'
          : 'move'
        : 'none';
    }
    return accepted;
  }

  function dragenter(event: DragEvent, target: T) {
    if (!options.enabled() || !takesPart(event)) return;
    consider(event, target);
    depth += 1;
  }

  function dragleave(event: DragEvent) {
    if (!options.enabled() || !takesPart(event)) return;
    const related = event.relatedTarget;
    depth = Math.max(0, depth - 1);
    if (related instanceof Node && element?.contains(related)) return;
    if (depth === 0) reset();
  }

  function drop(event: DragEvent, target: T) {
    if (!options.enabled() || !takesPart(event)) return;
    const accepted = options.canDrop(event, target);
    event.preventDefault();
    event.stopPropagation();
    reset();
    if (accepted) options.onDrop(event, target);
  }

  function isActive(target: T) {
    return activeTarget.value !== null && options.key(activeTarget.value) === options.key(target);
  }

  onBeforeUnmount(reset);
  return { activeTarget, label, isActive, dragenter, dragover: consider, dragleave, drop, reset };
}
